<template>
  <div class="ma-0 pa-0" v-if="routine.id">
    <v-row class="mb-2">
      <v-col cols="12" class="d-flex justify-end align-end">
        <ColumnsSelector
          v-if="!isLoading && tableOptions.totalRecordsInData > 0"
          persistenceKey="routineExecutionView_findingList"
          :headers="headers"
          @toggleColumn="onToggleColumn"
          @setInitialColumns="setInitialColumns"
        />
      </v-col>
    </v-row>

    <BulkActionsHeader
      v-if="showBulkActions"
      :selectedFindingsLength="selectedFindingsLength"
      :permissions="permissions"
      @bulkAddAssignees="$emit('addAssignees')"
      @bulkDueDate="$emit('editDueDate')"
      @bulkDelete="$emit('delete')"
    />

    <v-data-table
      v-show="!isLoading"
      data-private
      item-key="id"
      class="row-pointer"
      :show-select="hasPermissions"
      :value="selectedFindings"
      :headers="columns"
      :items="findings"
      :options.sync="localOptions"
      :server-items-length="tableOptions.totalRecordsInData"
      :loading="isLoading"
      :footer-props="{
        itemsPerPageOptions: tableOptions.itemsPerPageOptions,
      }"
      :no-data-text="$t('routines.findings_empty')"
      :hide-default-footer="!findings.length"
      @click:row="handleAddressClick"
      @input="$emit('setSelectedFindings', $event)"
    >
      <template v-slot:item.situation="{ item }">
        <p class="ma-0 pa-0 body-2" style="max-width: 300px">
          {{ item.situation }}
        </p>
      </template>
      <template v-slot:item.dueDate="{ item }">
        <p class="ma-0 pa-0 body-2">
          {{
            item.dueDate
              ? formatDate(item.dueDate, 'MMM DD yyyy')
              : $t('common.no_due_date_label')
          }}
        </p>
      </template>
      <template v-slot:item.assignees="{ item }">
        <div v-if="item.assignees.length">
          <ChipAvatar class="me-2 " small :account="item.assignees[0]" />
          <v-chip v-if="item.assignees.length > 1" pill small outlined>
            {{ '+ ' + (item.assignees.length - 1) }}
          </v-chip>
        </div>
        <p v-else class="ma-0 pa-0 body-2">
          {{ $t('routines.no_assignees') }}
        </p>
      </template>
      <template v-slot:item.status="{ item }">
        <FindingStatus :status="item.status" />
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="d-flex justify-end">
          <Button
            color="blueGrey"
            data-testid="findingList-button-editFinding"
            icon="mdi-pencil"
            :disabled="!canEditFinding({ routine, routineDefinition, finding: item })"
            @click.stop="handleEditClick(item.id)"
          />

          <Button
            color="blueGrey"
            data-testid="findingList-button-deleteFinding"
            icon="mdi-trash-can-outline"
            :disabled="!canDeleteFinding({ routine, routineDefinition, finding: item })"
            @click.stop="handleDeleteClick(item)"
          />
        </div>
      </template>
    </v-data-table>
    <TableSkeleton v-if="isLoading" />
    <v-row v-if="permissions.create" class="mb-0 mt-3 mx-0 px-0">
      <v-col cols="12" class="mx-0 px-0">
        <FindingComposer
          :routineDefinition="routineDefinition"
          :routine="routine"
          :selectedTagIds="selectedTagIds"
          @created="handleFindingCreated"
          @toggle-tag="handleToggleTag"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import mixin from '@/mixins/mixin'
import userMixin from '@/mixins/userMixin.vue'
import { utils } from 'frontend-js-shared'
import revMixin from '@/mixins/revMixin.vue'
import findingPermissions from '@/mixins/findingPermissions.vue'

import ChipAvatar from '@/components/ChipAvatar.vue'
import FindingComposer from '@/containers/RoutineExecutionView/FindingComposer.vue'
import BulkActionsHeader from '@/components/BulkActionsHeader.vue'
import Button from '@/components/UI/Button'
import ColumnsSelector from '@/components/ColumnsSelector.vue'
import TableSkeleton from '@/components/TableSkeleton.vue'
import FindingStatus from '../../components/FindingDrawer/FindingStatus.vue'

export default {
  name: 'RoutineExecutionFindingList',

  mixins: [mixin, userMixin, revMixin, findingPermissions],

  components: {
    ChipAvatar,
    FindingComposer,
    BulkActionsHeader,
    Button,
    ColumnsSelector,
    TableSkeleton,
    FindingStatus,
  },

  props: {
    routine: {
      type: Object,
      required: true,
    },
    routineDefinition: {
      type: Object,
      required: true,
    },
    isLoading: Boolean,
    findings: {
      type: Array,
      required: true,
    },
    tableOptions: {
      type: Object,
      required: true,
    },
    selectedFindings: {
      type: Array,
      default() {
        return []
      },
    },
  },

  data() {
    return {
      headers: [
        {
          text: this.$t('routines.finding_one'),
          align: 'start',
          sortable: true,
          value: 'situation',
          show: true,
        },
        {
          text: this.$t('common.due_date'),
          align: 'start',
          sortable: true,
          value: 'dueDate',
          width: '130px',
          show: true,
        },
        {
          text: this.$t('common.assignee_other'),
          align: 'start',
          sortable: true,
          value: 'assignees',
          show: true,
        },
        {
          text: this.$t('common.status'),
          align: 'start',
          sortable: true,
          value: 'status',
          width: '70px',
          show: true,
        },
        {
          text: this.$t('common.action_other'),
          align: 'end',
          sortable: false,
          value: 'actions',
          show: true,
        },
      ],
      selectedTagIds: [],
      situation: '',
    }
  },

  mounted() {
    if (this.$route?.params?.findingId) {
      this.$emit('openAddressFindingDrawer', this.$route?.params?.findingId)
    }
  },

  computed: {
    localOptions: {
      get() {
        return this.tableOptions
      },
      set(event) {
        this.$emit('getFindings', {
          page: event.page,
          itemsPerPage: event.itemsPerPage,
          sortBy: event.sortBy[0],
          sortDesc: event.sortDesc[0],
        })
      },
    },
    permissions() {
      return this.getPermissions({
        routine: this.routine,
        routineDefinition: this.routineDefinition,
      })
    },
    selectedFindingsLength() {
      return this.selectedFindings.length ?? 0
    },

    hasPermissions() {
      return this.permissions.edit || this.permissions.delete
    },

    showBulkActions() {
      return this.selectedFindingsLength > 0 && this.hasPermissions
    },

    columns() {
      return this.headers.filter(h => h.show)
    },
  },

  methods: {
    formatDate(date, format) {
      return utils.dates.formatDate(date, format)
    },

    handleAddressClick(finding) {
      this.$emit('openAddressFindingDrawer', finding.id)
    },

    handleEditClick(findingId) {
      this.$emit('openEditFindingDrawer', findingId)
    },

    handleDeleteClick(findingId) {
      this.$emit('openDeleteFindingModal', findingId)
    },

    handleFindingCreated() {
      this.selectedTagIds = []
      this.$emit('getFindings', { page: 1 })
    },

    handleToggleTag(tagId) {
      if (this.selectedTagIds.includes(tagId)) {
        this.selectedTagIds = this.selectedTagIds.filter(
          selectedTagId => selectedTagId !== tagId
        )
        return
      }

      this.selectedTagIds = [...this.selectedTagIds, tagId]
    },

    onToggleColumn({ index, value }) {
      this.headers[index].show = value
    },

    setInitialColumns(selectedColumns) {
      this.headers = this.getInitialSelectedTableColumns(
        this.headers,
        selectedColumns
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.row-pointer:hover {
  cursor: pointer;
}
</style>
